import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, Phone, Mail, MapPin } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { EnquiryForm } from "@/components/EnquiryForm";
import { BUSINESS } from "@/lib/site";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const RAILS = [
  { icon: MessageCircle, label: "WhatsApp Chat", value: BUSINESS.phoneDisplay, href: BUSINESS.whatsappUrl,
    note: "Fastest reply — ask for today's price", cls: "bg-emerald-600 hover:bg-emerald-500", testid: "contact-page-whatsapp" },
  { icon: Phone, label: "Call Direct", value: BUSINESS.phoneDisplay, href: `tel:${BUSINESS.phoneIntl}`,
    note: "Speak to us about stock and delivery", cls: "bg-blue-700 hover:bg-blue-600", testid: "contact-page-call" },
  { icon: Mail, label: "Email Us", value: BUSINESS.email, href: `mailto:${BUSINESS.email}`,
    note: "For detailed or bulk enquiries", cls: "bg-slate-800 hover:bg-slate-700", testid: "contact-page-email" },
];

export default function Contact() {
  const [searchParams] = useSearchParams();
  useEffect(() => { document.title = "Contact Us | SUDHEER ELECTRONIC"; }, []);
  return (
    <div className="bg-[#f4f7fa] text-slate-900 antialiased">
      <Navbar />

      <section className="py-16 sm:py-20 bg-white border-b border-slate-200" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-2xl">
            <span className="text-[11px] font-bold text-blue-700 tracking-widest uppercase">Contact Us</span>
            <h1 className="text-4xl sm:text-5xl font-black text-slate-950 tracking-tight leading-[1.1] mt-3 mb-6">
              Talk to SUDHEER ELECTRONIC
            </h1>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
              Prices, stock, delivery or bulk orders — reach us the way you like. WhatsApp is the fastest.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-[#f4f7fa]" data-testid="contact-rails-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {RAILS.map((r, i) => (
              <motion.a key={r.label} href={r.href} target={r.href.startsWith("http") ? "_blank" : undefined}
                rel="noopener noreferrer" {...fadeUp} transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={r.testid}
                className={`${r.cls} text-white rounded-2xl p-7 flex flex-col gap-3 shadow-md transition-colors`}>
                <r.icon size={26} />
                <span className="text-xs font-bold uppercase tracking-wider text-white/80">{r.label}</span>
                <span className="text-base font-bold break-words">{r.value}</span>
                <span className="text-xs text-white/70">{r.note}</span>
              </motion.a>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-16 items-start">
            <motion.div {...fadeUp} className="lg:col-span-5">
              <h2 className="text-2xl font-bold text-slate-950 mb-4">Visit the Store</h2>
              <p className="text-sm text-slate-600 leading-relaxed mb-6">
                Customers come directly to our shop in the local area for personal assistance, live product demos,
                warranty bills, and immediate pickup. Call or WhatsApp ahead to confirm stock and store hours.
              </p>
              <div className="p-6 rounded-2xl bg-white border border-slate-200 space-y-3 text-sm" data-testid="contact-info-card">
                <p className="flex items-center gap-3"><MapPin size={16} className="text-blue-700 flex-shrink-0" /><span><span className="font-bold">Service Area:</span> Local area — customers come to the shop</span></p>
                <p className="flex items-center gap-3"><Phone size={16} className="text-blue-700 flex-shrink-0" /><span><span className="font-bold">Phone / WhatsApp:</span> {BUSINESS.phoneDisplay}</span></p>
                <p className="flex items-center gap-3"><Mail size={16} className="text-blue-700 flex-shrink-0" /><span className="break-words"><span className="font-bold">Email:</span> {BUSINESS.email}</span></p>
              </div>
            </motion.div>
            <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.1 }} className="lg:col-span-7">
              <EnquiryForm initialProduct={searchParams.get("product") || ""} />
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
